import { useCallback } from 'react';
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useFormContext,
} from 'react-hook-form';
import { createYupAugmentedResolver } from '../../resolver/createYupAugmentedResolver.ts';
import { useFormAugmentedContext } from '../augmented/FormAugmentedContext.tsx';

export interface IHandleDraftSubmitParams {
  onValid: (data: any) => Promise<void>;
  extraContext?: Record<string, any>;
}

/**
 * Permette di chiamare da codice il submit del form. Ritorna un handler che gestisce la costruzione
 * della funzione di submit in maniera speculare a quello che viene fatto `Form.tsx`
 */
export function useHandleSubmit(params: IHandleDraftSubmitParams) {
  const { onValid, extraContext } = params;

  const augmented = useFormAugmentedContext();
  const methods = useFormContext();

  const handler = useCallback(async () => {
    // Creo il contesto di validazione estendendolo con possibili parametri aggiuntivi
    const validationContext = {
      ...augmented.validationContext,
      ...extraContext,
    };

    // Creo le due funzioni che verranno chiamate in caso di validazione corretta o errata
    const handleValid: SubmitHandler<any> = async rawValues => {
      augmented.invalidSubject.next({ type: 'valid', data: rawValues });
      const values = await augmented.schema.validate(rawValues, {
        context: validationContext,
      });

      await onValid(values);
    };

    const handleInvalid: SubmitErrorHandler<any> = errors => {
      augmented.invalidSubject.next({ type: 'invalid', errors });
    };

    // Creo il resolver allo stesso modo di useForm, necessario per ritornare la bag degli errori.
    const resolver = createYupAugmentedResolver(augmented.schema);

    // Recupero i valori del form
    const values = methods.getValues();

    // Valido i dati con lo stesso contesto ma con un tipo di validazione diverso (draft)
    const result = await resolver(values, validationContext, {
      abortEarly: false,
    });

    // Se non ci sono errori, chiamo la funzione di validazione corretta, altrimenti chiamo quella di errore
    if (Object.keys(result.errors).length === 0) {
      await handleValid(result.values);
      methods.clearErrors();
    } else {
      // La validazione draft non dovrebbe in linea di massima restituire errori, ma nel caso
      // in cui ciò accada, li imposto sul form così che siano gestiti.
      Object.keys(result.errors).forEach(key => {
        const error = (result.errors as any)[key];
        methods.setError(key, error);
      });
      await handleInvalid(result.errors);
    }
  }, [
    augmented.invalidSubject,
    augmented.schema,
    augmented.validationContext,
    extraContext,
    methods,
    onValid,
  ]);

  return handler;
}
